var ApplicationViewService_1;
import { __decorate } from "tslib";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { RouterParamsService } from "../../shared/services/route-params.service";
import { ApiStepMetaConstants } from "../../shared/constants/api-constants/api-step-meta.constants";
import { map } from "rxjs/operators";
import { ApiLookupConstants } from "../../shared/constants/api-constants/api-lookup.constants";
import { TrueCentroidResultsModel } from "../../shared/models/gpserver-truecentroid-model";
import { ApiAttachmentConstants } from "../../shared/constants/api-constants/api-attachment.constants";
import { ApiNotesConstants } from "../../shared/constants/api-constants/api-notes.constants";
import { AuthService } from "../../shared/services/auth/auth.service";
import { ApiModuleMapConstants } from "../../shared/constants/api-constants/api-module-map.constants";
import { AutoUnsubPortal } from "../../shared/decorators/autounsub-portal.decorator";
import { ApiRecipientConstants } from "../../shared/constants/api-constants/api-recipient.constants";
import { ApiCommunicationConstants } from "../../shared/constants/api-constants/api-communication.constants";
import { ApiAdminConstants } from "../../shared/constants/api-constants/api-admin.constants";
import { ApiCommentConstants } from "../../shared/constants/api-constants/api-comment.constants";
let ApplicationViewService = class ApplicationViewService {
    static { ApplicationViewService_1 = this; }
    http;
    authService;
    routeParamsService;
    moduleID;
    propertyData; // {data: ""};
    propertySubject = new BehaviorSubject(undefined);
    propertyObservable = this.propertySubject.asObservable();
    startMetadataSubject = new BehaviorSubject(null);
    $startMetadata = this.startMetadataSubject.asObservable();
    selectedModuleName = new BehaviorSubject("");
    selectedModuleNameAsObservable = this.selectedModuleName.asObservable();
    selectGoToStep = new BehaviorSubject(0);
    selectGoToStepAsObservable = this.selectGoToStep.asObservable();
    hasLoadedLookups = new BehaviorSubject(false);
    hasLoadedLookupsAsObservable = this.hasLoadedLookups.asObservable();
    fullApplicationCheckRequired = new BehaviorSubject(false);
    $fullApplicationCheckRequired = this.fullApplicationCheckRequired.asObservable();
    $missingLookupsForZones = [];
    $clearLayerGraphicsSubject = new BehaviorSubject(false);
    routeParamsSubscription;
    constructor(http, authService, routeParamsService) {
        this.http = http;
        this.authService = authService;
        this.routeParamsService = routeParamsService;
    }
    static checkIfChanged(newValue, oldValue) {
        if (!newValue) {
            return null;
        }
        return newValue !== oldValue;
    }
    savePropertyData(property) {
        this.propertyData.data = property;
        this.propertySubject.next(this.propertyData.data);
    }
    setHasLoadedLookups(loaded) {
        this.hasLoadedLookups.next(loaded);
    }
    start() {
        this.routeParamsSubscription = this.routeParamsService.params.subscribe(async (params) => {
            await this.handleRouteParamChange(params);
        });
    }
    async getStepMeta() {
        const filterString = `stepName eq \'Start\' AND moduleId eq ${this.moduleID}`;
        const params = {
            $filter: filterString
        };
        await this.http.get(`${ApiStepMetaConstants.URL}/${ApiStepMetaConstants.FUNCTION_GET_STEP_META_FOR_AFLA_PORTAL}`, { params }).subscribe((metadata) => {
            this.startMetadataSubject.next(JSON.parse(metadata.value[0].metadata));
        });
    }
    setFullApplicationCheckRequired(required) {
        this.fullApplicationCheckRequired.next(required);
    }
    setModuleName(moduleName) {
        this.selectedModuleName.next(moduleName);
    }
    setSelectGoToStep(stepIndex) {
        this.selectGoToStep.next(stepIndex);
    }
    getLookupsObservable(params) {
        return this.http.get(`${ApiLookupConstants.URL}/${ApiLookupConstants.FUNCTION_GET_LOOKUPS_FOR_AFLA_PORTAL}`, { params }).pipe(map(lookups => {
            return lookups;
        }));
    }
    deleteUploadDocumentsObservable(params) {
        return this.http.post(`${ApiAttachmentConstants.URL}/${ApiAttachmentConstants.FUNCTION_DELETE_MULTIPLE_ATTACHMENTS}`, params).pipe(map(attachment => {
            return attachment;
        }));
    }
    getTrueCentroid(url, params) {
        return this.http.post(url, params).pipe(map((response) => {
            return new TrueCentroidResultsModel().deserialize(response);
        }));
    }
    sendMissingLookupsEmail(url, params) {
        return this.http.post(url, params)
            .pipe(map(response => {
            return response;
        }));
    }
    async handleRouteParamChange(params) {
        if (params !== undefined && params !== null && params && ApplicationViewService_1.checkIfChanged(params.moduleid, this.moduleID)) {
            this.moduleID = params.moduleid;
            await this.getStepMeta();
        }
    }
    getAttachments(params) {
        return this.http.get(`${ApiAttachmentConstants.URL}/${ApiAttachmentConstants.FUNCTION_GET_MY_AFLA_PORTAL_DOCUMENTS}`, { params }).pipe(map(lookups => {
            return lookups;
        }));
    }
    getApplicationRecipients(params) {
        return this.http.get(ApiRecipientConstants.URL, { params }).pipe(map(response => {
            return response;
        }));
    }
    getCommunications(params) {
        return this.http.get(`${ApiCommunicationConstants.URL}/${ApiCommunicationConstants.FUNCTION_GET_COMMUNICATION_BY_JOBID}`, { params }).pipe(map(response => {
            return response;
        }));
    }
    getComments(params) {
        return this.http.get(`${ApiCommentConstants.URL}/${ApiCommentConstants.FUNCTION_GET_COMMENTS_BY_JOBID}`, { params }).pipe(map(response => {
            return response;
        }));
    }
    getIsApplicationClosed(moduleId, jobId) {
        return this.http.get(`${ApiAdminConstants.URL}/${ApiAdminConstants.FUNCTION_IS_APPLICATION_CLOSED}?moduleId=${moduleId}&jobId=${jobId}`);
    }
    getNotesPostObservable(params) {
        return this.http.get(ApiNotesConstants.URL, { params });
    }
    getCemeterySelectionModuleMaps(moduleId) {
        const params = {
            $filter: `moduleId eq ${moduleId}`,
            $orderby: "mapOrder desc"
        };
        return this.http.get(`${ApiModuleMapConstants.URL}/${ApiModuleMapConstants.FUNCTION_GET_CEMETERY_SELECTION_MODULE_MAP_FOR_AFLA_PORTAL}`, { params });
    }
    queryMapViaProxy(url, moduleMapId) {
        const params = {
            MapServiceId: moduleMapId, Authorization: this.authService.getAuthToken()
        };
        // @ts-ignore
        return this.http.get(url, { headers: params });
    }
    static ctorParameters = () => [
        { type: HttpClient },
        { type: AuthService },
        { type: RouterParamsService }
    ];
};
ApplicationViewService = ApplicationViewService_1 = __decorate([
    Injectable({
        providedIn: "root"
    }),
    AutoUnsubPortal()
], ApplicationViewService);
export { ApplicationViewService };
